import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "@tanstack/react-router"
import { ChevronDown, Loader2, LogIn, ShieldCheck, Lock, Truck, Activity, ArrowLeft } from "lucide-react"
import { useState } from "react"

import { ACCOUNT_TYPES, loginSchema, type LoginFormValues } from "@/lib/schemas/auth.schema"
import { useLogin } from "@/hooks/auth/useLogin"
import { Button } from "@/components/ui/button"
import { useDevice } from "@/hooks/useIsMobile"

import { AuthLeftPanel } from "./AuthLeftPanel"

export function LoginForm() {
  const { isDesktop } = useDevice()
  const [showPassword, setShowPassword] = useState(false)
  const [isForcedAdmin, setIsForcedAdmin] = useState(false)
  const login = useLogin()

  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "")
  const rawRole = searchParams.get("role") || searchParams.get("accountType")
  const registeredEmail = searchParams.get("email") || ""
  const isJustRegistered = searchParams.get("registered") === "true"

  const isAdminFromUrl = rawRole?.toUpperCase() === "ADMIN"
  const isAdmin = isAdminFromUrl || isForcedAdmin

  const initialRole = (isAdminFromUrl
    ? "ADMIN"
    : rawRole?.toUpperCase() === "SELLER"
    ? "SELLER"
    : "BUYER") as LoginFormValues["role"]

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: { role: initialRole, email: registeredEmail, password: "" },
  })

  const onSubmit = (values: LoginFormValues) => {
    login.mutate(values)
  }

  return (
    <div className="min-h-[100dvh] lg:min-h-screen flex flex-col lg:flex-row w-full bg-[#f5f5f5]">
      
      {/* Left Panel: Branding & Benefits */}
      {isDesktop && <AuthLeftPanel />}

      {/* Right Panel: Login Form */}
      <main className="w-full lg:w-1/2 bg-[#f5f5f5] flex flex-col h-[100dvh] lg:h-screen p-4 sm:p-6 overflow-y-auto">
        {/* Top-Left Back Navigation (Positioned at the top of the page on Mobile & Tablet, Hidden on Desktop) */}
        <div className="w-full lg:hidden shrink-0">
          <Link
            to="/"
            className="group inline-flex items-center gap-1.5 text-xs font-semibold text-secondary hover:text-[#be001e] transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be001e]/30 rounded-md p-1"
          >
            <ArrowLeft className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Login Card Container (Centered with space between top navigation and card) */}
        <div className="w-full max-w-[380px] mx-auto my-auto py-6 pb-20 sm:pb-8 lg:py-0">
          <div className="bg-white w-full rounded-xl p-6 border-05 border-outline-variant flex flex-col gap-4.5 shadow-sm">
          {/* Form Header */}
          <header className="text-center md:text-left">
            <h1 className="font-sans text-xl font-black text-on-surface">Sign in to Enga</h1>
          </header>

          {/* Registration Success Notification Banner */}
          {isJustRegistered && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 font-medium">
              Account created successfully! Please sign in to complete your profile setup.
            </div>
          )}



          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            {/* Role Select field */}
            {isAdmin ? (
              <input type="hidden" value="ADMIN" {...register("role")} />
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="login-role">
                  Role
                </label>
                <div className="relative">
                  <select
                    id="login-role"
                    aria-invalid={!!errors.role}
                    className="w-full h-9 px-3 pr-10 border-05 border-outline-variant rounded-lg text-[16px] sm:text-xs bg-surface text-on-surface appearance-none focus:outline-none focus:border-primary focus:ring-0 cursor-pointer"
                    {...register("role")}
                  >
                    {ACCOUNT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute inset-y-0 right-3 my-auto size-3.5 text-secondary" />
                </div>
                {errors.role && (
                  <p className="text-[11px] text-primary font-medium">{errors.role.message}</p>
                )}
              </div>
            )}

            {/* Email Field */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="login-email">
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="name@gmail.com"
                autoComplete="email"
                aria-invalid={!!errors.email}
                className="w-full h-9 px-3 border-05 border-outline-variant rounded-lg text-[16px] sm:text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-[11px] text-primary font-medium">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1 relative">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold uppercase tracking-wider text-secondary" htmlFor="login-password">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] font-semibold text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  className="w-full h-9 px-3 pr-12 border-05 border-outline-variant rounded-lg text-[16px] sm:text-xs bg-surface text-on-surface focus:outline-none focus:border-primary focus:ring-0"
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-primary uppercase tracking-wider hover:underline focus:outline-none cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {errors.password && (
                <p className="text-[11px] text-primary font-medium">{errors.password.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              id="login-submit"
              type="submit"
              disabled={login.isPending}
              className="w-full h-9 bg-primary-container text-on-primary-container font-semibold rounded-lg text-xs transition-all duration-200 active:scale-[0.98] flex items-center justify-center hover:bg-primary-container/95 cursor-pointer mt-1"
            >
              {login.isPending ? (
                <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              ) : (
                <LogIn className="size-3.5 mr-1.5" />
              )}
              {login.isPending ? "Signing in…" : "Sign in"}
            </Button>
          </form>


          {/* Registration Footer */}
          <footer className="text-center flex flex-col gap-2">
            <p className="text-secondary text-xs">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-bold text-primary hover:underline"
              >
                Create one
              </Link>
            </p>

            {!isAdmin && (
              <>
                <div className="flex items-center my-2 text-[10px] text-slate-300 font-bold select-none">
                  <div className="flex-grow border-t border-slate-200"></div>
                  <span className="px-3 uppercase tracking-widest text-[9px]">OR</span>
                  <div className="flex-grow border-t border-slate-200"></div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsForcedAdmin(true)
                    setValue("role", "ADMIN")
                  }}
                  className="text-[10px] font-bold uppercase tracking-wider text-secondary hover:text-primary hover:underline cursor-pointer bg-transparent border-none p-0 outline-none"
                >
                  Internal Team?
                </button>
              </>
            )}
          </footer>
        </div>
      </div>
    </main>

    </div>
  )
}
